import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { CalculationStore } from '../../../../../stores/calculation.store';
import { CalculationError } from '../../../../../../../../../../../common/src/errors/calculations/calculation.error';
import {
  calculationErrorMetadata,
} from '../../../../../../../../../../../common/src/metadatas/calculation-error.metadata';
import { TranslateService } from '@ngx-translate/core';
import {
  CalculationResponse,
} from '../../../../../../../../../../../common/dist/angular/responses/calculation.response';
import {
  SalaryDividendOptions,
} from '../../../../../../../../../../../common/src/options/calculations/salary-dividend/salary-dividend.options';
import {
  SalaryDividendResult,
} from '../../../../../../../../../../../common/dist/angular/results/calculations/salary-dividend/salary-dividend.result';
import { CategoryScale, Chart, LinearScale, LineController, LineElement, PointElement, Tooltip } from 'chart.js';
import {
  CalculationStatusEnum,
} from '../../../../../../../../../../../common/src/enums/calculations/calculation-status.enum';
import { MoneyUtil } from '../../../../../../../../../../../common/src/utils/money.util';
import { LocaleService } from '../../../../../../advanced-auth/services/locale.service';
import annotationPlugin from 'chartjs-plugin-annotation';


@Component({
  selector: 'app-salary-dividend-result',
  templateUrl: './salary-dividend-result.component.html',
  styleUrls: ['./salary-dividend-result.component.scss'],
  standalone: false,
})
export class SalaryDividendResultComponent implements OnInit, OnDestroy {
  CalculationStatusEnum = CalculationStatusEnum;
  public calculationId: string;
  public calculationResponse: CalculationResponse;
  public options: SalaryDividendOptions;
  public results: SalaryDividendResult;
  isSectionPersonalLiquidityCollapsed: boolean = false;
  isSectionCorporateDisbursementCollapsed: boolean = false;
  isSectionNote1Collapsed: boolean = false;
  isSectionNote2Collapsed: boolean = false;
  isSectionNote3Collapsed: boolean = false;
  selectedBadge: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  chartElement: HTMLCanvasElement | undefined;
  chart: Chart | undefined;
  private calculationResponseSubscription: Subscription;

  constructor(private readonly calculationStore: CalculationStore,
              private readonly translateService: TranslateService,
              private readonly localeService: LocaleService,
              private readonly activatedRoute: ActivatedRoute) {

    // Start by setting the calculationId
    this.calculationId = this.activatedRoute.snapshot.params.calculationId;

  }

  private _calculationStatusEnum: CalculationStatusEnum;

  get calculationStatusEnum(): CalculationStatusEnum {
    return this._calculationStatusEnum;
  }

  set calculationStatusEnum(value: CalculationStatusEnum) {
    this._calculationStatusEnum = value;
  }

  ngOnInit(): void {
    this.activatedRoute.parent.params.subscribe(params => {
      if (params.calculationId === undefined) {
        return;
      }

      this.calculationId = params.calculationId;

      // Unsubscribe all the subscriptions on route change.
      this.unsubscribeAll();

      this.calculationResponseSubscription = this.calculationStore.get(params.calculationId).subscribe(async (calculationResponse) => {
        this.calculationStatusEnum = calculationResponse?.status ?? this.calculationStatusEnum;

        // todo: add a mechanism to know if the results are simply loading (when calculationResponse === undefined) or there's an error (the other two conditions)
        if (calculationResponse === undefined || calculationResponse.options === undefined || calculationResponse.results === undefined) {
          return;
        }


        this.calculationResponse = calculationResponse;
        this.options = calculationResponse.options.options as SalaryDividendOptions;
        this.results = calculationResponse.results.results as SalaryDividendResult;

        await this.initGraph();
        await this.updateGraph();
      });
    });


    // this.calculationResponse = exampleResponse;
    // this.options = this.calculationResponse.options.options as SalaryDividendOptions;
    // this.results = this.calculationResponse.results.results as SalaryDividendResult;
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  public async initGraph() {
    if (this.chartElement !== undefined) {
      return;
    }

    Chart.register(annotationPlugin);
    Chart.register(Tooltip);
    Chart.register(CategoryScale);
    Chart.register(LinearScale);
    Chart.register(LineController);
    Chart.register(PointElement);
    Chart.register(LineElement);

    this.chartElement = document.getElementById('graph') as HTMLCanvasElement;
    this.chart = new Chart(this.chartElement, {
      type: 'line',
      data: {
        datasets: [],
      },
      options: {
        scales: {
          y: {
            title: {
              display: true,
              text: 'Déboursés corporatifs',
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return (context.raw as any).description;
              },
            },
          },
        },
      },
    });

    window.addEventListener('beforeprint', () => {
      document.getElementById('graph-container')!.style.width = '825px';

      // HIDE CHAT BOX ON PRINT
      const liveFrame = document.querySelector('#hubspot-messages-iframe-container > iframe');
      if (liveFrame != null) {
        // @ts-ignore
        liveFrame.style.setProperty('display', 'none', 'important');
      }
    });
    window.addEventListener('afterprint', () => {
      document.getElementById('graph-container')!.style.width = 'auto';

      const liveFrame = document.querySelector('#hubspot-messages-iframe-container > iframe');
      if (liveFrame != null) {
        // @ts-ignore
        liveFrame.style.removeProperty('display');
      }
    });
  }

  public async updateGraph() {
    const numberFormat = new Intl.NumberFormat(await this.localeService.getLocaleForUser(), {
      style: 'currency',
      currency: 'CAD',
      currencyDisplay: 'narrowSymbol',

    });

    const graphData = (this.calculationResponse.results.results as SalaryDividendResult).allOtherPoints.sort((a, b) => {
      return b.percentage - a.percentage;
    });

    // Get the optimium point of cashDisbursement and show a line at this point
    const optimumValue = MoneyUtil.centsToDollars((this.calculationResponse.results.results as SalaryDividendResult).optimum.corporate.cashDisbursement);
    let percentageThatMatchesOptimum = 0;

    this.chart.data.labels = graphData.map(item => {
      const percentage = Math.round(item.percentage * 100);

      if (optimumValue === MoneyUtil.centsToDollars(item.cashDisbursement)) {
        percentageThatMatchesOptimum = 100 - percentage;
      }

      if (percentage === 100) {
        return `100% Salaire - 0% Dividende`;
      } else if (percentage === 50) {
        return `50% Salaire - 5 0% Dividende`;
      } else if (percentage === 0) {
        return `0% Salaire - 100% Dividende`;
      }

      // We inverse since we want to start with 100% salary and 0% dividend
      return 100 - percentage + '';
    });

    this.chart.data.datasets = [{
      borderColor: '#1C7ED6',
      pointBackgroundColor: '#005098',
      data: graphData.map(item => {
        return {
          x: 100 - Math.round(item.percentage * 100),
          y: MoneyUtil.centsToDollars(item.cashDisbursement),
          description: `Salaire: ${numberFormat.format(MoneyUtil.centsToDollars(item.salary))}, Dividende: ${numberFormat.format(MoneyUtil.centsToDollars(item.dividend))}, Déboursé corporatif: ${numberFormat.format(MoneyUtil.centsToDollars(item.cashDisbursement))}`,
        };
      }),
    }];


    this.chart.options.plugins.annotation = {
      annotations: {
        line1: {
          type: 'line',
          yMin: optimumValue,
          yMax: optimumValue,
          borderColor: '#B1C2D9',
          borderWidth: 2,
          borderDash: [10, 10],

          label: {
            content: 'Valeur optimale',
            backgroundColor: '#B1C2D9',
            color: 'white',
            display: true,
          },
        },
        line2: {
          type: 'line',
          xMin: percentageThatMatchesOptimum,
          xMax: percentageThatMatchesOptimum,
          borderColor: '#B1C2D9',
          borderWidth: 2,
          borderDash: [10, 10],
          label: {
            content: 'Valeur optimale',
            backgroundColor: '#B1C2D9',
            color: 'white',
            display: true,
          },
        },
      },
    };

    this.chart?.update();
  }

  getErrorText(error: string | CalculationError) {
    if (typeof error === 'string') {
      return error;
    }

    if (error.hasOwnProperty('calculationErrorKeyname')) {
      const calculationError = calculationErrorMetadata[error.calculationErrorKeyname];

      if (calculationError === undefined) {
        return 'Erreur inconnue'; // todo: translate this.
      }

      if (calculationError.i18n.hasOwnProperty(this.translateService.currentLang) && calculationError.i18n[this.translateService.currentLang] !== '') {
        return calculationError.i18n[this.translateService.currentLang];
      }

      return calculationError.i18n.fr;
    }
  }

  private unsubscribeAll() {
    this.calculationResponseSubscription?.unsubscribe();
  }
}
